import { useState } from "react";
import { z } from "zod";
import { Loader2, Search, FileText, X, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  searchDriveFiles,
  downloadDriveFile,
  PDF_MIME,
  DOCX_MIME,
  type DriveFile,
} from "@/lib/googleDriveSearch";

const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20MB matches local upload limit

const keywordSchema = z
  .string()
  .trim()
  .min(1, "Enter a keyword")
  .max(60, "Keyword too long");

interface DriveSearchCardProps {
  onFileSelected: (file: File) => void;
  onCancel: () => void;
  onFallbackToFullPicker: () => void;
}

export function DriveSearchCard({
  onFileSelected,
  onCancel,
  onFallbackToFullPicker,
}: DriveSearchCardProps) {
  const { toast } = useToast();
  const [keyword, setKeyword] = useState("");
  const [mimeChoice, setMimeChoice] = useState<"pdf" | "docx">("pdf");
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [results, setResults] = useState<DriveFile[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    const parsed = keywordSchema.safeParse(keyword);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Invalid keyword");
      return;
    }
    setError(null);
    setLoading(true);
    setResults(null);

    try {
      const mimeType = mimeChoice === "pdf" ? PDF_MIME : DOCX_MIME;
      const files = await searchDriveFiles({ keyword: parsed.data, mimeType });
      setResults(files);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      toast({
        title: "Search failed",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePick = async (file: DriveFile) => {
    setDownloadingId(file.id);
    try {
      const downloaded = await downloadDriveFile(
        file.id,
        file.name,
        file.mimeType
      );
      if (downloaded.size > MAX_FILE_BYTES) {
        toast({
          title: "File too large",
          description: "Files must be under 20 MB.",
          variant: "destructive",
        });
        setDownloadingId(null);
        return;
      }
      onFileSelected(downloaded);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Download failed",
        description: message,
        variant: "destructive",
      });
      setDownloadingId(null);
    }
  };

  const formatSize = (bytes?: string) => {
    if (!bytes) return "";
    const n = Number(bytes);
    if (!Number.isFinite(n)) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  const formatDate = (iso?: string) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return "";
    }
  };

  return (
    <Card className="p-5 bg-card/90 backdrop-blur-sm border-card-border">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Search className="h-4 w-4 text-primary" />
            Find your resume in Google Drive
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Type a few words from the file name and pick the file type.
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onCancel}
          aria-label="Close"
          className="h-7 w-7"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-3">
        <div>
          <Label htmlFor="drive-keyword" className="text-xs">
            Keywords from your file name
          </Label>
          <Input
            id="drive-keyword"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder='e.g. "resume", "cv 2024", "john"'
            maxLength={60}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
            disabled={loading}
            className="mt-1"
          />
          {error && (
            <p className="text-xs text-destructive mt-1">{error}</p>
          )}
        </div>

        <div>
          <Label className="text-xs">File type</Label>
          <RadioGroup
            value={mimeChoice}
            onValueChange={(v) => setMimeChoice(v as "pdf" | "docx")}
            className="flex gap-4 mt-1"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="pdf" id="drive-pdf" />
              <Label htmlFor="drive-pdf" className="text-sm cursor-pointer">
                PDF
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="docx" id="drive-docx" />
              <Label htmlFor="drive-docx" className="text-sm cursor-pointer">
                DOCX
              </Label>
            </div>
          </RadioGroup>
        </div>

        <Button
          onClick={handleSearch}
          disabled={loading || !keyword.trim()}
          className="w-full"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Searching your Drive...
            </>
          ) : (
            <>
              <Search className="h-4 w-4 mr-2" />
              Search my Drive
            </>
          )}
        </Button>
      </div>

      {results && (
        <div className="mt-4 border-t border-border pt-4">
          {results.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-3">
                Nothing matched "{keyword}". Try different keywords or open the
                full picker.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={onFallbackToFullPicker}
              >
                <ExternalLink className="h-3 w-3 mr-2" />
                Open full Drive picker
              </Button>
            </div>
          ) : (
            <ul className="space-y-1.5 max-h-72 overflow-y-auto">
              {results.map((file) => (
                <li key={file.id}>
                  <button
                    onClick={() => handlePick(file)}
                    disabled={downloadingId !== null}
                    className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-accent transition text-left disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {file.iconLink ? (
                      <img
                        src={file.iconLink}
                        alt=""
                        className="w-5 h-5 flex-shrink-0"
                      />
                    ) : (
                      <FileText className="w-5 h-5 flex-shrink-0 text-muted-foreground" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        {file.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDate(file.modifiedTime)}
                        {file.size && ` · ${formatSize(file.size)}`}
                      </div>
                    </div>
                    {downloadingId === file.id && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}
